import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { peoplePulseDataService } from '../utils/peoplePulseDataService';
import '../../../HR/components/styles/HRDashboard.css';
import '../styles/peoplepulse.css';

function PerformanceReviews() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('cycles');

  const [reviewCycles, setReviewCycles] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [submissions, setSubmissions] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const [showCreateCycleModal, setShowCreateCycleModal] = useState(false);
  const [showCreateTemplateModal, setShowCreateTemplateModal] = useState(false);
  const [setShowReviewModal] = useState(false);
  const [setSelectedReview] = useState(null);

  const [cycleFormData, setCycleFormData] = useState({
    name: '',
    startDate: '',
    endDate: '',
    templateId: '',
    participants: [],
    type: 'annual',
    status: 'draft'
  });

  const [templateFormData, setTemplateFormData] = useState({
    name: '',
    description: '',
    sections: [{ title: '', questions: [{ text: '', type: 'rating' }] }]
  });

  const [filters, setFilters] = useState({
    search: '',
    status: '',
    type: ''
  });

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      setLoading(true);
      const [cyclesData, templatesData, submissionsData] = await Promise.all([
        peoplePulseDataService.getReviewCycles(),
        peoplePulseDataService.getReviewTemplates(),
        peoplePulseDataService.getReviewSubmissions()
      ]);

      setReviewCycles(cyclesData || []);
      setTemplates(templatesData || []);
      setSubmissions(submissionsData || []);
      setError(null);
    } catch (err) {
      console.error('Failed to load review data:', err);
      setError('Failed to load reviews: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCycle = async (e) => {
    e.preventDefault();
    try {
      const newCycle = await peoplePulseDataService.createReviewCycle(cycleFormData);
      setReviewCycles([newCycle, ...reviewCycles]);
      setShowCreateCycleModal(false);
      resetCycleForm();
      setSuccessMessage('Review cycle created successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Failed to create cycle: ' + err.message);
    }
  };

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    try {
      const newTemplate = await peoplePulseDataService.createReviewTemplate(templateFormData);
      setTemplates([newTemplate, ...templates]);
      setShowCreateTemplateModal(false);
      resetTemplateForm();
      setSuccessMessage('Template created successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Failed to create template: ' + err.message);
    }
  };

  // const handleSubmitReview = async (reviewId, responses) => {
  //   try {
  //     await peoplePulseDataService.submitReview(reviewId, { responses, status: 'submitted' });
  //     setSuccessMessage('Review submitted successfully!');
  //     setTimeout(() => setSuccessMessage(''), 3000);
  //     setShowReviewModal(false);
  //     loadAllData();
  //   } catch (err) {
  //     setError('Failed to submit review: ' + err.message);
  //   }
  // };

  const handleLaunchCycle = async (cycleId) => {
    if (!window.confirm('Are you sure you want to launch this review cycle?')) return;

    try {
      await peoplePulseDataService.updateReviewCycle(cycleId, { status: 'active' });
      setReviewCycles(reviewCycles.map(c => c.id === cycleId ? { ...c, status: 'active' } : c));
      setSuccessMessage('Review cycle launched!');
      setTimeout(() => setSuccessMessage(''), 2000);
    } catch (err) {
      setError('Failed to launch cycle: ' + err.message);
    }
  };

  const handleCloseCycle = async (cycleId) => {
    if (!window.confirm('Are you sure you want to close this review cycle?')) return;

    try {
      await peoplePulseDataService.updateReviewCycle(cycleId, { status: 'completed' });
      setReviewCycles(reviewCycles.map(c => c.id === cycleId ? { ...c, status: 'completed' } : c));
      setSuccessMessage('Review cycle closed!');
      setTimeout(() => setSuccessMessage(''), 2000);
    } catch (err) {
      setError('Failed to close cycle: ' + err.message);
    }
  };

  const addSection = () => {
    setTemplateFormData({
      ...templateFormData,
      sections: [...templateFormData.sections, { title: '', questions: [{ text: '', type: 'rating' }] }]
    });
  };

  const removeSection = (index) => {
    const newSections = templateFormData.sections.filter((_, i) => i !== index);
    setTemplateFormData({ ...templateFormData, sections: newSections });
  };

  const addQuestion = (sectionIndex) => {
    const newSections = [...templateFormData.sections];
    newSections[sectionIndex].questions.push({ text: '', type: 'rating' });
    setTemplateFormData({ ...templateFormData, sections: newSections });
  };

  const removeQuestion = (sectionIndex, questionIndex) => {
    const newSections = [...templateFormData.sections];
    newSections[sectionIndex].questions = newSections[sectionIndex].questions.filter((_, i) => i !== questionIndex);
    setTemplateFormData({ ...templateFormData, sections: newSections });
  };

  const resetCycleForm = () => {
    setCycleFormData({
      name: '',
      startDate: '',
      endDate: '',
      templateId: '',
      participants: [],
      type: 'annual',
      status: 'draft'
    });
  };

  const resetTemplateForm = () => {
    setTemplateFormData({
      name: '',
      description: '',
      sections: [{ title: '', questions: [{ text: '', type: 'rating' }] }]
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'draft': '#6C757D',
      'active': '#3B82F6',
      'completed': '#10B981',
      'cancelled': '#EF4444',
      'pending': '#F59E0B',
      'submitted': '#7B68EE'
    };
    return colors[status] || '#6C757D';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getCompletionPercentage = (cycle) => {
    if (!cycle.totalParticipants || cycle.totalParticipants === 0) return 0;
    return Math.round((cycle.completedReviews || 0) / cycle.totalParticipants * 100);
  };

  if (loading) {
    return (
      <div className="hr-dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading reviews...</p>
      </div>
    );
  }

  const filteredCycles = reviewCycles.filter(cycle => {
    if (filters.search && !cycle.name.toLowerCase().includes(filters.search.toLowerCase())) return false;
    if (filters.status && cycle.status !== filters.status) return false;
    if (filters.type && cycle.type !== filters.type) return false;
    return true;
  });

  return (
    <div className="hr-dashboard-container peoplepulse-page">
      <div className="hr-dashboard-header">
        <div>
          <h1>Performance Reviews</h1>
          <p className="subtitle">Manage review cycles, templates, and submissions</p>
        </div>
        <div className="header-actions">
          {activeTab === 'cycles' && (
            <button className="btn-primary" onClick={() => setShowCreateCycleModal(true)}>
              + Create Review Cycle
            </button>
          )}
          {activeTab === 'templates' && (
            <button className="btn-primary" onClick={() => setShowCreateTemplateModal(true)}>
              + Create Template
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success">
          <span>{successMessage}</span>
        </div>
      )}

      {/* Tabs */}
      <div className="tabs-container">
        <button
          className={`tab ${activeTab === 'cycles' ? 'active' : ''}`}
          onClick={() => setActiveTab('cycles')}
        >
          Review Cycles ({reviewCycles.length})
        </button>
        <button
          className={`tab ${activeTab === 'templates' ? 'active' : ''}`}
          onClick={() => setActiveTab('templates')}
        >
          Templates ({templates.length})
        </button>
        <button
          className={`tab ${activeTab === 'submissions' ? 'active' : ''}`}
          onClick={() => setActiveTab('submissions')}
        >
          My Reviews ({submissions.length})
        </button>
      </div>

      {/* Review Cycles Tab */}
      {activeTab === 'cycles' && (
        <>
          {/* Filters */}
          <div className="filters-bar">
            <input
              type="text"
              placeholder="Search cycles..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="search-input"
            />

            <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
              <option value="">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>

            <select value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })}>
              <option value="">All Types</option>
              <option value="annual">Annual</option>
              <option value="quarterly">Quarterly</option>
              <option value="mid-year">Mid-Year</option>
              <option value="probation">Probation</option>
            </select>

            <button className="btn-secondary" onClick={() => setFilters({ search: '', status: '', type: '' })}>
              Clear Filters
            </button>
          </div>

          {/* Cycles Grid */}
          <div className="reviews-grid">
            {filteredCycles.length === 0 ? (
              <div className="empty-state">
                <p>No review cycles found</p>
                <button className="btn-primary" onClick={() => setShowCreateCycleModal(true)}>
                  Create Your First Cycle
                </button>
              </div>
            ) : (
              filteredCycles.map((cycle) => (
                <div key={cycle.id} className="review-cycle-card">
                  <div className="cycle-header">
                    <div>
                      <h3>{cycle.name}</h3>
                      <span className="badge" style={{ backgroundColor: getStatusColor(cycle.status) }}>
                        {cycle.status}
                      </span>
                      <span className="badge cycle-type">{cycle.type}</span>
                    </div>
                  </div>

                  <div className="cycle-dates">
                    <div className="date-item">
                      <label>Start Date</label>
                      <span>{formatDate(cycle.startDate)}</span>
                    </div>
                    <div className="date-item">
                      <label>End Date</label>
                      <span>{formatDate(cycle.endDate)}</span>
                    </div>
                  </div>

                  <div className="cycle-progress">
                    <div className="progress-header">
                      <span>Completion</span>
                      <span className="progress-value">{getCompletionPercentage(cycle)}%</span>
                    </div>
                    <div className="progress-bar-container">
                      <div
                        className="progress-bar-fill"
                        style={{
                          width: `${getCompletionPercentage(cycle)}%`,
                          backgroundColor: '#3B82F6'
                        }}
                      ></div>
                    </div>
                    <small>{cycle.completedReviews || 0} of {cycle.totalParticipants || 0} reviews completed</small>
                  </div>

                  <div className="cycle-actions">
                    {cycle.status === 'draft' && (
                      <button className="btn-primary small" onClick={() => handleLaunchCycle(cycle.id)}>
                        Launch Cycle
                      </button>
                    )}
                    {cycle.status === 'active' && (
                      <button className="btn-secondary small" onClick={() => handleCloseCycle(cycle.id)}>
                        Close Cycle
                      </button>
                    )}
                    <button className="btn-link" onClick={() => navigate(`/teamflect/reviews/cycle/${cycle.id}`)}>
                      View Details
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div className="templates-grid">
          {templates.length === 0 ? (
            <div className="empty-state">
              <p>No templates found</p>
              <button className="btn-primary" onClick={() => setShowCreateTemplateModal(true)}>
                Create Your First Template
              </button>
            </div>
          ) : (
            templates.map((template) => (
              <div key={template.id} className="template-card">
                <div className="template-header">
                  <h3>{template.name}</h3>
                  {template.isDefault && <span className="badge default-badge">Default</span>}
                </div>
                <p className="template-description">{template.description}</p>
                <div className="template-stats">
                  <span>{template.sections?.length || 0} sections</span>
                  <span>{template.totalQuestions || 0} questions</span>
                  <span>Used in {template.usageCount || 0} cycles</span>
                </div>
                <div className="template-actions">
                  <button className="btn-link" onClick={() => navigate(`/teamflect/reviews/template/${template.id}`)}>
                    View Template
                  </button>
                  <button className="btn-secondary small">Edit</button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Submissions Tab */}
      {activeTab === 'submissions' && (
        <div className="submissions-list">
          {submissions.length === 0 ? (
            <div className="empty-state">
              <p>No reviews assigned to you</p>
            </div>
          ) : (
            submissions.map((submission) => (
              <div key={submission.id} className="submission-card">
                <div className="submission-header">
                  <div>
                    <h3>{submission.employeeName}</h3>
                    <span className="submission-cycle">{submission.cycleName}</span>
                  </div>
                  <span className="badge" style={{ backgroundColor: getStatusColor(submission.status) }}>
                    {submission.status}
                  </span>
                </div>

                <div className="submission-info">
                  <div className="info-item">
                    <label>Review Type</label>
                    <span>{submission.reviewType}</span>
                  </div>
                  <div className="info-item">
                    <label>Due Date</label>
                    <span>{formatDate(submission.dueDate)}</span>
                  </div>
                  {submission.submittedAt && (
                    <div className="info-item">
                      <label>Submitted</label>
                      <span>{formatDate(submission.submittedAt)}</span>
                    </div>
                  )}
                </div>
                
                <div className="submission-actions">
                  {submission.status === 'pending' ? (
                    <button className="btn-primary" onClick={() => {
                      setSelectedReview(submission);
                      setShowReviewModal(true);
                    }}>
                      Complete Review
                    </button>
                  ) : (
                    <button className="btn-link" onClick={() => navigate(`/teamflect/reviews/submission/${submission.id}`)}>
                      View Review
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Create Cycle Modal */}
      {showCreateCycleModal && (
        <div className="modal-overlay" onClick={() => setShowCreateCycleModal(false)}>
          <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Review Cycle</h2>
              <button onClick={() => { setShowCreateCycleModal(false); resetCycleForm(); }}>×</button>
            </div>

            <form onSubmit={handleCreateCycle}>
              <div className="form-group">
                <label>Cycle Name *</label>
                <input
                  type="text"
                  value={cycleFormData.name}
                  onChange={(e) => setCycleFormData({ ...cycleFormData, name: e.target.value })}
                  required
                  placeholder="e.g., 2025 Annual Performance Review"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Review Type *</label>
                  <select
                    value={cycleFormData.type}
                    onChange={(e) => setCycleFormData({ ...cycleFormData, type: e.target.value })}
                    required
                  >
                    <option value="annual">Annual Review</option>
                    <option value="quarterly">Quarterly Review</option>
                    <option value="mid-year">Mid-Year Review</option>
                    <option value="probation">Probation Review</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Template *</label>
                  <select
                    value={cycleFormData.templateId}
                    onChange={(e) => setCycleFormData({ ...cycleFormData, templateId: e.target.value })}
                    required
                  >
                    <option value="">Select template...</option>
                    {templates.map(template => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Start Date *</label>
                  <input
                    type="date"
                    value={cycleFormData.startDate}
                    onChange={(e) => setCycleFormData({ ...cycleFormData, startDate: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>End Date *</label>
                  <input
                    type="date"
                    value={cycleFormData.endDate}
                    onChange={(e) => setCycleFormData({ ...cycleFormData, endDate: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => { setShowCreateCycleModal(false); resetCycleForm(); }}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Create Cycle
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Template Modal */}
      {showCreateTemplateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateTemplateModal(false)}>
          <div className="modal-content large-modal scrollable-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Review Template</h2>
              <button onClick={() => { setShowCreateTemplateModal(false); resetTemplateForm(); }}>×</button>
            </div>

            <form onSubmit={handleCreateTemplate}>
              <div className="form-group">
                <label>Template Name *</label>
                <input
                  type="text"
                  value={templateFormData.name}
                  onChange={(e) => setTemplateFormData({ ...templateFormData, name: e.target.value })}
                  required
                  placeholder="e.g., Standard Performance Review"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={templateFormData.description}
                  onChange={(e) => setTemplateFormData({ ...templateFormData, description: e.target.value })}
                  rows="2"
                  placeholder="Describe this template..."
                />
              </div>

              <div className="sections-container">
                <div className="section-header">
                  <h3>Sections & Questions</h3>
                  <button type="button" className="btn-secondary small" onClick={addSection}>
                    + Add Section
                  </button>
                </div>

                {templateFormData.sections.map((section, sectionIndex) => (
                  <div key={sectionIndex} className="section-form">
                    <div className="section-title-row">
                      <input
                        type="text"
                        value={section.title}
                        onChange={(e) => {
                          const newSections = [...templateFormData.sections];
                          newSections[sectionIndex].title = e.target.value;
                          setTemplateFormData({ ...templateFormData, sections: newSections });
                        }}
                        placeholder="Section title..."
                        required
                        className="section-title-input"
                      />
                      <button
                        type="button"
                        className="btn-danger small"
                        onClick={() => removeSection(sectionIndex)}
                        disabled={templateFormData.sections.length === 1}
                      >
                        Remove Section
                      </button>
                    </div>

                    <div className="questions-list">
                      {section.questions.map((question, questionIndex) => (
                        <div key={questionIndex} className="question-form">
                          <input
                            type="text"
                            value={question.text}
                            onChange={(e) => {
                              const newSections = [...templateFormData.sections];
                              newSections[sectionIndex].questions[questionIndex].text = e.target.value;
                              setTemplateFormData({ ...templateFormData, sections: newSections });
                            }}
                            placeholder="Question text..."
                            required
                          />
                          <select
                            value={question.type}
                            onChange={(e) => {
                              const newSections = [...templateFormData.sections];
                              newSections[sectionIndex].questions[questionIndex].type = e.target.value;
                              setTemplateFormData({ ...templateFormData, sections: newSections });
                            }}
                          >
                            <option value="rating">Rating (1-5)</option>
                            <option value="text">Text Response</option>
                            <option value="yesno">Yes/No</option>
                          </select>
                          <button
                            type="button"
                            className="btn-danger small"
                            onClick={() => removeQuestion(sectionIndex, questionIndex)}
                            disabled={section.questions.length === 1}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        className="btn-link"
                        onClick={() => addQuestion(sectionIndex)}
                      >
                        + Add Question
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => { setShowCreateTemplateModal(false); resetTemplateForm(); }}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Create Template
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default PerformanceReviews;


